import { useState, useEffect } from "react";
import { X, Network, Link2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const KIND_META = {
  rj45: { label: "Porty RJ45 (Ethernet)", color: "text-emerald-400" },
  sfp: { label: "Porty SFP / SFP+", color: "text-cyan-400" },
};

export default function SwitchView({ sw, role, onClose, onRefresh }) {
  const canEdit = role !== "odczyt";
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null); // {kind, no, key}
  const [af, setAf] = useState({ target_label: "", target_port: "", note: "" });

  const reload = () => api.get(`/switches/${sw.id}/ports`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sw.id]);

  const attach = async () => {
    if (!af.target_label) { toast.error("Podaj nazwę urządzenia/celu"); return; }
    try {
      await api.post(`/switches/${sw.id}/port-attach`, { key: sel.key, target_label: af.target_label, target_port: af.target_port ? Number(af.target_port) : null, note: af.note });
      toast.success("Podłączono do portu"); setAf({ target_label: "", target_port: "", note: "" }); reload(); onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  };
  const clearPort = async () => {
    try { await api.post(`/switches/${sw.id}/port-clear`, { key: sel.key }); toast.success("Wyczyszczono port"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  };

  const renderSection = (kind) => {
    const arr = data[kind] || [];
    if (!arr.length) return null;
    const M = KIND_META[kind];
    return (
      <div className="mb-4" data-testid={`switch-sec-${kind}`}>
        <div className={`text-sm font-heading font-bold mb-2 flex items-center gap-2 ${M.color}`}><Network size={15} /> {M.label} ({arr.length})</div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
          {arr.map((p) => {
            const active = sel && sel.key === p.key;
            const occ = !!p.attach;
            return (
              <button key={p.key} onClick={() => setSel({ kind, no: p.no, key: p.key })} data-testid={`switch-port-${p.key}`}
                className={`rounded-md border px-1.5 py-1.5 text-left transition-colors ${active ? "ring-2 ring-cyan-400" : ""} ${occ ? "bg-red-900/25 border-red-700/50 hover:bg-red-900/40" : "bg-green-900/15 border-green-700/40 hover:bg-green-900/30"}`}>
                <div className="text-[11px] font-mono text-slate-300">{kind === "sfp" ? "SFP " : "RJ45 "}{p.no}</div>
                {p.attach && <div className="text-[9px] text-blue-300 truncate mt-0.5">→ {p.attach.target_label}</div>}
                {!occ && <div className="text-[9px] text-green-400 mt-0.5">wolny</div>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const selData = sel && (data[sel.kind] || []).find((p) => p.key === sel.key);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="switch-view">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Network size={18} className="text-amber-400" /> Switch {sw.name}</h3>
          <button onClick={onClose} data-testid="switch-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        {!data ? <div className="p-6 text-slate-500 text-sm">Ładowanie…</div> : (
          <div className="overflow-y-auto p-5">
            <div className="text-xs text-slate-400 mb-3">{sw.producent || ""} {sw.model || ""} {sw.ip_address ? `· ${sw.ip_address}` : ""}{sw.poe === "Tak" ? " · PoE" : ""}</div>
            {renderSection("rj45")}
            {renderSection("sfp")}
            {(!data.rj45.length && !data.sfp.length) && (
              <div className="text-sm text-slate-500">Brak zdefiniowanych portów. Uzupełnij w karcie switcha: „Porty RJ45" i „Porty SFP/SFP+".</div>
            )}

            {sel && (
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4 mt-3" data-testid="switch-port-editor">
                <div className="text-sm font-heading font-bold text-cyan-300 mb-2">{KIND_META[sel.kind].label.replace("Porty ", "")} {sel.no}</div>
                {canEdit && (
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 mb-1 flex items-center gap-1"><Link2 size={12} /> Podłącz urządzenie do portu</div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={af.target_label} onChange={(e) => setAf((f) => ({ ...f, target_label: e.target.value }))} placeholder="Nazwa (np. ODF, uplink, kamera)" data-testid="switch-attach-label" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                      <input type="number" min="1" value={af.target_port} onChange={(e) => setAf((f) => ({ ...f, target_port: e.target.value }))} placeholder="Port docelowy (opcj.)" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                    </div>
                    <button onClick={attach} data-testid="switch-attach-apply" className="mt-2 bg-blue-600 hover:bg-blue-500 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><Link2 size={14} /> Podłącz</button>
                    {selData?.attach && <button onClick={clearPort} data-testid="switch-clear-port" className="mt-2 ml-3 text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"><Trash2 size={12} /> Wyczyść port</button>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
